import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { ChatBubble } from 'app/design/icons/chat-bubble';
import React, { useEffect } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path, Rect } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import { IconTrash, IconEdit } from '@tabler/icons-react-native';
import { createTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createTheme();

interface MessageMenuProps {
  onCopy: () => void | Promise<void>;
  onReply: () => void;
  onDelete: () => void;
  onEdit: () => void;
  canCopy?: boolean;
  canDelete?: boolean;
  canEdit?: boolean;
  width?: number;
}

function CopyIcon({ color = '#1F2635', size = 20 }: { color?: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect transform="translate(9, 9)" width={11} height={11} rx={2} stroke={color} strokeWidth={1.5} />
      <Path d="M5 15V5a2 2 0 012-2h10" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function MenuItem({
  onPress,
  icon,
  label,
  color = '#1F2635',
}: {
  onPress: () => void;
  icon: React.ReactNode;
  label: string;
  color?: string;
}) {
  const scale = useSharedValue(1);
  const bgOpacity = useSharedValue(0);

  const handlePress = () => {
    scale.value = withSequence(withTiming(0.92, { duration: 80 }), withTiming(1, { duration: 80 }));
    bgOpacity.value = withSequence(withTiming(0.08, { duration: 80 }), withTiming(0, { duration: 150 }));
    setTimeout(onPress, 120);
  };

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    backgroundColor: `rgba(0, 0, 0, ${String(bgOpacity.value)})`,
  }));

  return (
    <Animated.View style={animatedStyle}>
      <Pressable onPress={handlePress} style={styles.menuItem}>
        {icon}
        <Text style={[styles.menuLabel, { color }]}>{label}</Text>
      </Pressable>
    </Animated.View>
  );
}

export function MessageMenu({
  onCopy,
  onReply,
  onDelete,
  onEdit,
  canCopy = true,
  canDelete = true,
  canEdit = false,
  width = 200,
}: MessageMenuProps) {
  const { t } = useTranslation('one-channel');
  const containerScale = useSharedValue(0.8);
  const containerOpacity = useSharedValue(0);

  useEffect(() => {
    containerScale.value = withSpring(1, { damping: 14, stiffness: 180, mass: 0.5 });
    containerOpacity.value = withSpring(1, { damping: 14, stiffness: 180, mass: 0.5 });
  }, [containerScale, containerOpacity]);

  const containerStyle = useAnimatedStyle(() => ({
    opacity: containerOpacity.value,
    transform: [{ scale: containerScale.value }],
    minWidth: 200,
    width: width,
  }));

  return (
    <Animated.View style={containerStyle}>
      <Column
        style={{
          backgroundColor: theme.colors['surface.tertiary-neutral'],
          borderRadius: 12,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <MenuItem
          onPress={onReply}
          icon={<ChatBubble color="#1F2635" width={20} height={20} />}
          label={t('menu.reply')}
        />

        {canCopy ? (
          <>
            <View style={styles.separator} />
            <MenuItem
              onPress={() => void onCopy()}
              icon={<CopyIcon color="#1F2635" size={20} />}
              label={t('menu.copy')}
            />
          </>
        ) : null}

        {canEdit ? (
          <>
            <View style={styles.separator} />
            <MenuItem
              onPress={onEdit}
              icon={<IconEdit strokeWidth={ICON_STROKE_WIDTH} color="#1F2635" size={20} />}
              label={t('menu.edit')}
              color="#1F2635"
            />
          </>
        ) : null}

        {canDelete ? (
          <>
            <View style={styles.separator} />
            <MenuItem
              onPress={onDelete}
              icon={
                <IconTrash strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} size={20} />
              }
              label={t('menu.delete')}
              color={theme.colors['surface.error.default']}
            />
          </>
        ) : null}
      </Column>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  menuLabel: {
    fontSize: 16,
    color: '#1F2635',
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#C0C0C0',
    marginHorizontal: 16,
  },
});
